import React, { useMemo, useState, useEffect } from 'react';
import {
  Button,
  Form,
  Radio,
  RadioGroup,
  Input,
  Modal,
  notification,
} from 'tea-component';
import { getLocale } from 'umi';
import moment from 'moment';
import useLanguage from '@op/hooks/useLanguage';
import { CONTACT_EMAIL } from '@op/constants';
import SvgIcon from '@home/components/SvgIcon';
import { asyncCreateShortUrl } from '@op/components/FilterCommon/utils/historyUtils';
import { gameInfoType } from '@op/types/common';
import SingleDateTimePicker from '../SingleDateTimePicker';
import './index.scss';
import { useCreateKeywordAnalysis } from '@op/pages/AIAnalysisHub/KeywordAndURLAnalysis/hook/useTopicAnalysisFlow';
import { regularMaximumDuration } from '@op/pages/AIAnalysisHub/KeywordAndURLAnalysis/constants';
import { getTimeLimitation } from '@op/components/FilterCommon/utils';
import { DBO_TM } from '@op/utils/format';
import { DBO_POINT } from '@op/operationLog/buriedPoint';

type TFrequency = 'hourly' | 'daily' | 'weekly' | 'biweekly' | 'monthly';

interface IRegularListeningData {
  frequency: TFrequency;
  endTime: string;
  name: string;
  firstReportTime: [string, string];
}

interface IRegularListeningSettingProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (data: IRegularListeningData) => void;
  defaultName?: string;
  currentFilter?: any;
  gameInfo?: gameInfoType;
  category?: 'normal' | 'topic'; // 任务分类
  isEdit?: boolean;
  request?: () => void;
  hasPermission?: boolean;
}

const FREQUENCY_OPTIONS: Array<{ value: TFrequency; label: string }> = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  // { value: 'biweekly', label: 'Biweekly' },
  // { value: 'monthly', label: 'Monthly' },
];

// 实时时间显示组件 - 独立组件避免父组件重渲染
const RealTimeClock: React.FC = () => {
  const [time, setTime] = useState(moment().format('YYYY-MM-DD HH:mm:ss'));

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(moment().format('YYYY-MM-DD HH:mm:ss'));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return <>{time}</>;
};

export default function RegularListeningSetting({
  visible,
  onClose,
  onConfirm,
  defaultName = '',
  currentFilter,
  gameInfo,
  category = 'normal',
  isEdit = false,
  request,
  hasPermission,
}: IRegularListeningSettingProps) {
  const lang = getLocale();
  const { formatMessage } = useLanguage();
  const [frequency, setFrequency] = useState<TFrequency>('daily');
  const [reportName, setReportName] = useState(defaultName);
  const [isFirst, setIsFirst] = useState(true);
  const { createAnalysisTask, loading, updateAnalysisTask } =
    useCreateKeywordAnalysis({ showError: false, isCreator: true });

  // 根据当前 filter 获取默认的开始和结束时间
  const defaultTimeRange = useMemo(() => {
    const { start_time: startTime, end_time: endTime } = currentFilter || {};
    // 从 filter 中获取时间范围
    if (startTime && endTime) {
      return {
        start: moment(startTime),
        end: moment(endTime),
      };
    }
    // 默认值：最近一天
    return {
      start: moment().subtract(1, 'day').startOf('day'),
      end: moment(),
    };
  }, [currentFilter]);

  // 报告开始时间（用户选择的），默认值从 filter 的 start_time 获取
  const [reportStartTime, setReportStartTime] = useState<string>(
    defaultTimeRange.start.format('YYYY-MM-DD HH:mm:ss'),
  );

  // 当 defaultTimeRange 变化时更新 reportStartTime
  useEffect(() => {
    setReportStartTime(defaultTimeRange.start.format('YYYY-MM-DD HH:mm:ss'));
  }, [defaultTimeRange]);

  // 根据当前 filter 获取默认结束时间（一年后）
  const defaultEndTime = useMemo(() => moment().add(1, 'year'), []);

  const [endTime, setEndTime] = useState<string>(
    moment(defaultEndTime).format('YYYY-MM-DD HH:mm:ss'),
  );

  // 根据频率和开始时间计算首份报告时间范围的结束时间
  // 规则：
  // - 开始时间：用户选择的值（reportStartTime）
  // - 结束时间：
  //   - hourly: 当前时间减1小时
  //   - 其他: 前一天
  // const getFirstReportTimeRangeEnd = (
  //   selectTime: moment.Moment,
  //   freq: TFrequency,
  // ) => {
  //   const timeNumber = {
  //     hourly: [1, 'hour'],
  //     daily: [1, 'day'],
  //     weekly: [7, 'day'],
  //     biweekly: [14, 'day'],
  //     monthly: [30, 'day'],
  //   };
  //   // 当前时间点
  //   const nowPoint = DBO_TM.utc();
  //   // 以开始时间和周期频率计算的结束时间
  //   const endTime = selectTime.clone().add(...timeNumber[freq]);

  //   // 结束时间晚于当前时间
  //   const isAfterNow = endTime.isAfter(nowPoint);
  //   if (isAfterNow) {
  //     return [selectTime, endTime.subtract(1, 's')];
  //   }

  //   // 切割点
  //   let splicePoint = moment()
  //     .hours(selectTime.hours())
  //     .minutes(selectTime.minutes())
  //     .seconds(selectTime.seconds());

  //   if (freq === 'hourly') {
  //     splicePoint = moment().hours(nowPoint.hours()).minutes(0).seconds(0);
  //   }

  //   return [selectTime, splicePoint.subtract(1, 's')];
  // };

  // 计算首份报告时间范围（用于显示）
  const firstReportTimeRange = useMemo((): [string, string] => {
    if (!reportStartTime) {
      return ['', ''];
    }
    // 选中时间 < 当前时间 isBeforeNow: [选中时间, 当前时间]
    // 选中时间 >= 当前时间 !isBeforeNow: [选中时间, 选中时间 + N]
    const startMoment = moment(reportStartTime);
    const endMoment = moment();
    // const [startTime, endTime] = getFirstReportTimeRangeEnd(
    //   startMoment,
    //   frequency,
    // );

    const format = 'YYYY-MM-DD HH:mm:ss';
    return [startMoment.format(format), endMoment.format(format)];
  }, [reportStartTime, frequency]);

  // 结束时间变更
  const onChangeEndTime = (value: string) => {
    setEndTime(value);
  };

  // 报告开始时间变更
  const onChangeReportStartTime = (value: string) => {
    setReportStartTime(value);
  };

  const nameError = useMemo(() => {
    if (isFirst) {
      return null;
    }
    if (!reportName.trim()) {
      return formatMessage({ id: "Can't be empty" });
    }
    return null;
  }, [reportName, isFirst, formatMessage]);

  const handleConfirm = async () => {
    setIsFirst(false);
    if (!reportName.trim()) return;
    DBO_POINT.btn('RegularListeningConfirm');
    if (isEdit) {
      const editParams = {
        id: currentFilter?.id,
        task_name: reportName,
        keyword_analysis_task_type: 'recurring',
        short_url: currentFilter?.short_url,
        first_report_start_time: currentFilter.first_report_start_time,
        first_report_end_time: currentFilter.first_report_end_time,
        recurrence_type: frequency,
        end_time: endTime,
      };
      const editData = await updateAnalysisTask(editParams);
      if (editData?.task_id) {
        notification.success({
          description: formatMessage({ id: 'Edit Success' }),
        });
        request?.();
      }
      handleClose();
      return;
    }

    try {
      // 1. 创建 shortUrl
      let shortId = '';
      if (currentFilter && gameInfo) {
        shortId = await asyncCreateShortUrl({
          filter: currentFilter,
          gameInfo,
          updateUrl: false,
          errorText: formatMessage({ id: 'Failed to create short URL' }),
        });
      }

      // 2. 使用新接口创建周期任务
      // 处理参数：topic 类型需要 query 参数，不传 keyword_list
      const { promptInfo, gptList, ...restFilter } = currentFilter || {};
      const taskParams: any = {
        ...restFilter, // 展开 CommonFilter 参数（已排除 keyword 和 keyword_list）
        task_name: reportName.trim(),
        keyword_analysis_task_type: 'recurring',
        keyword_analysis_task_category: category,
        short_url: shortId,
        first_report_start_time: firstReportTimeRange[0], // 用户选择的开始时间
        first_report_end_time: moment().format('YYYY-MM-DD HH:mm:ss'), // 当前时间前一天或前一小时
        recurrence_type: frequency, // 周期类型
        end_time: endTime, // 用户选择的结束时间
        ...gameInfo,
      };
      const data = await createAnalysisTask(taskParams);

      if (data.task_id) {
        // 3. 调用父组件的确认回调
        onConfirm({
          frequency,
          endTime,
          name: reportName.trim(),
          firstReportTime: firstReportTimeRange,
        });

        handleClose();
        notification.success({
          description: formatMessage('Report generation success.'),
        });
        return;
      }

      notification.error({
        description: formatMessage(
          { id: 'Report generation failed. Please contact {email}.' },
          { email: CONTACT_EMAIL },
        ),
      });
    } catch (error) {
      console.error('Failed to create keyword analysis task:', error);
      // 这里可以添加错误提示
    }
  };

  const handleClose = () => {
    setErrorTips({ error: false, tips: '' });
    setFrequency('daily');
    setIsFirst(true);
    // 重置结束时间
    setEndTime(moment(defaultEndTime).format('YYYY-MM-DD HH:mm:ss'));
    // 重置报告开始时间为 filter 的 start_time
    setReportStartTime(defaultTimeRange.start.format('YYYY-MM-DD HH:mm:ss'));
    onClose();
  };

  // 根据频率获取下份报告生成时间提示
  const getNextReportTip = (freq: TFrequency): string => {
    switch (freq) {
      case 'hourly':
        return `${formatMessage({
          id: 'Subsequent updates will be triggered at the top of each hour.',
        })}`;
      case 'daily':
        return `${formatMessage({
          id: 'Subsequent updates will be triggered daily at 00:00 (UTC+8).',
        })}`;
      case 'weekly':
        return `${formatMessage({
          id: 'Subsequent updates will be triggered every 7 days at 00:00 (UTC+8).',
        })}`;
      case 'biweekly':
        return `14${formatMessage({ id: 'days' })}`;
      case 'monthly':
        return `1${formatMessage({ id: 'month' })}`;
      default:
        return '';
    }
  };

  const [errorTips, setErrorTips] = useState({ error: false, tips: '' });

  // 检测开始时间是否大于当前时间
  useEffect(() => {
    if (!visible) return;

    const checkStartTime = () => {
      const startMoment = moment(reportStartTime);
      const now = moment();

      if (startMoment.isAfter(now)) {
        setErrorTips({
          error: true,
          tips: formatMessage({
            id: 'Report start time cannot be later than current time',
          }),
        });
        return true;
      }
      return false;
    };

    // 先检查开始时间
    if (checkStartTime()) return;

    // 原有的权限和时间范围检查
    if (!hasPermission && category === 'topic') {
      const { defaultNum, defaultUnit, tips } =
        regularMaximumDuration(formatMessage);
      const isFlag = getTimeLimitation(
        [reportStartTime, moment().format('YYYY-MM-DD HH:mm:ss')],
        defaultNum,
        defaultUnit as any,
      );

      const timer = setTimeout(() => {
        setErrorTips({ error: isFlag, tips });
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    }

    // 清除错误
    setErrorTips({ error: false, tips: '' });
  }, [reportStartTime, formatMessage, visible, hasPermission, category]);

  useEffect(() => {
    if (isEdit) {
      let intervalType: TFrequency = 'daily';
      if (currentFilter?.interval_unit === 'month') {
        intervalType = 'monthly';
      } else if (currentFilter?.interval_unit === 'week') {
        intervalType = 'weekly';
      } else if (currentFilter?.interval_unit === 'hour') {
        intervalType = 'hourly';
      }
      setReportName(currentFilter?.name);
      setFrequency(intervalType || 'daily');
      setReportStartTime(currentFilter?.first_report_start_time);
      setEndTime(currentFilter?.cycle_end_date);
    }
  }, [isEdit, currentFilter]);

  return (
    <Modal
      visible={visible}
      caption={formatMessage({
        id: isEdit ? 'Edit Repeat Settings' : 'Generate and Repeat Settings',
      })}
      onClose={handleClose}
      disableEscape
      size="l"
      className="dbo-regular-listening"
    >
      <Modal.Body>
        {/* 顶部提示信息 */}
        <div className="info-text">
          {formatMessage(
            'The update only refreshes the topic tagging results; please go to the report page to trigger the sentiment summary as needed.',
          )}
        </div>

        <Form
          className={`dbo-regular-listening-setting ${
            isEdit ? 'dbo-filterbox-preview' : ''
          }`}
        >
          {/* Report Start Time - 首份报告时间 */}
          <Form.Item
            label={
              <div>
                {formatMessage({ id: 'Report Start Time' })}
                <br /> (UTC+{DBO_TM.timezone()})
              </div>
            }
            className="regular-listening-item"
          >
            <div className="first-report-time-wrapper">
              <SingleDateTimePicker
                value={reportStartTime}
                onChange={onChangeReportStartTime}
                language={lang === 'zh-CN' ? 'cn' : 'en'}
                showTime
                error={errorTips.error}
                tips={errorTips.tips}
                disabledDate={(date) => {
                  if (!date) return false;
                  const todayStart = moment().startOf('day');
                  return date.valueOf() <= todayStart.valueOf();
                }}
                disabledTime={(date) => {
                  if (!date) return {};

                  const now = moment();

                  if (date.isSame(now, 'day')) {
                    return {
                      disabledHours: () =>
                        Array.from({ length: 24 }, (_, i) => i).filter(
                          (h) => h > now.hour(),
                        ),

                      disabledMinutes: (hour: number) =>
                        hour === now.hour()
                          ? Array.from({ length: 60 }, (_, i) => i).filter(
                              (m) => m > now.minute(),
                            )
                          : [],

                      disabledSeconds: (hour: number, minute: number) =>
                        hour === now.hour() && minute === now.minute()
                          ? Array.from({ length: 60 }, (_, i) => i).filter(
                              (s) => s > now.second(),
                            )
                          : [],
                    };
                  }

                  return {};
                }}
              />
            </div>
            {/* <div className="frequency-description">
              <span className="description-text">
                {`${formatMessage({ id: 'First Report Time Range:' })} ${moment(
                  firstReportTimeRange[0],
                ).format('YYYY-MM-DD HH:mm:ss')} ${formatMessage({
                  id: 'to',
                })} `}
                <RealTimeClock />
                {`. `}
                {formatMessage({ id: 'Start immediately' })}
              </span>
            </div> */}
          </Form.Item>

          {/* Update Frequency - 更新频率 */}
          <Form.Item
            label={formatMessage({ id: 'Update Frequency' })}
            className="regular-listening-item"
          >
            <RadioGroup
              value={frequency}
              onChange={(value) => setFrequency(value as TFrequency)}
            >
              {FREQUENCY_OPTIONS.map((option) => {
                if (
                  option.value === 'hourly' &&
                  !hasPermission &&
                  category === 'topic'
                ) {
                  return null;
                }
                return (
                  <Radio key={option.value} name={option.value}>
                    {formatMessage({ id: option.label })}
                  </Radio>
                );
              })}
            </RadioGroup>
            <div className="frequency-description">
              <span className="description-text">
                {getNextReportTip(frequency)}
              </span>
            </div>
          </Form.Item>

          {/* Repeat End Time - 重复结束时间 */}
          <Form.Item
            label={formatMessage({ id: 'Repeat End Time' })}
            className="regular-listening-item"
          >
            <SingleDateTimePicker
              value={endTime}
              onChange={onChangeEndTime}
              language={lang === 'zh-CN' ? 'cn' : 'en'}
              showTime
              disabledDate={(date) => {
                // 禁用今天之前的日期
                return date.isAfter(moment().startOf('day'));
              }}
            />
            {/* {frequency !== 'hourly' && category === 'topic' && (
              <div className="frequency-description">
                <span className="description-text">
                  {formatMessage(
                    'If three consecutive data reports generated based on the update frequency are not accessed, the reports will automatically stop',
                  )}
                </span>
              </div>
            )} */}
          </Form.Item>

          {/* Name - 名称 */}
          <Form.Item
            required
            showStatusIcon={false}
            label={formatMessage({ id: 'Report Name' })}
            className="regular-listening-item"
            status={nameError ? 'error' : undefined}
            message={nameError}
          >
            <div className="name-input-wrapper">
              <Input
                size="full"
                value={reportName}
                onChange={(value) => setReportName(value)}
                placeholder={formatMessage({
                  id: 'Please enter a report name',
                })}
              />
              {reportName && reportName.trim() !== '' && (
                <SvgIcon
                  type="clear-icon"
                  className="name-input-clear-icon"
                  onClick={() => setReportName('')}
                />
              )}
            </div>
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          disabled={!reportName.trim() || loading || errorTips.error}
          loading={loading}
          onClick={() => void handleConfirm()}
        >
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={handleClose}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
